import { useEffect } from 'react';
import {
  useAccountsConfigurations,
  getRegularAccountsAssets,
  type AccountConfigurationCodeDto,
  type AccountsMarginRestsBaseResponseDto,
  type AccountsRestsBaseResponseDto,
  type AccountsRestsByType,
} from '@/entities/account';
import {
  useOpenAccountAvailabilityStore,
  type OpenAccountCodeAvailability,
} from './useOpenAccountAvailabilityStore';
import {
  usePendingAccountsStore,
  type PendingAccount,
} from './pendingAccountsStore';

type OpenedCounts = {
  total: number;
  byAsset: Record<string, number>;
};

const PENDING_TYPE_BY_CONFIG_TYPE: Record<
  string,
  PendingAccount['accountType'] | undefined
> = {
  spot: 'spot',
  invest: 'invest',
  leverage: 'margin',
  earn: 'earn',
};

const addAssetCount = (counts: OpenedCounts, assetCode?: string) => {
  if (!assetCode) return;
  const code = assetCode.toLowerCase();
  counts.byAsset[code] = (counts.byAsset[code] || 0) + 1;
};

const countPendingForCode = (
  counts: OpenedCounts,
  pendingAccounts: Array<PendingAccount>,
  pendingType: PendingAccount['accountType'] | undefined,
  accountCode: string
) => {
  if (!pendingType) return;
  pendingAccounts
    .filter(
      pending =>
        pending.accountType === pendingType &&
        (!pending.accountCode ||
          pending.accountCode.toLowerCase() === accountCode.toLowerCase())
    )
    .forEach(pending => {
      counts.total += 1;
      addAssetCount(counts, pending.assetCode);
    });
};

const countMarginAccountsForCode = (
  rests: AccountsMarginRestsBaseResponseDto | null,
  accountCode: string
): OpenedCounts => {
  const counts: OpenedCounts = { total: 0, byAsset: {} };
  const codeLower = accountCode.toLowerCase();

  rests?.accounts
    .filter(account => account.tradingProductCode?.toLowerCase() === codeLower)
    .forEach(account => {
      counts.total += 1;
      addAssetCount(counts, account.balanceAsset?.code);
    });

  return counts;
};

const countSpotAccounts = (
  spotRests: Array<AccountsRestsBaseResponseDto>
): OpenedCounts => {
  const counts: OpenedCounts = { total: spotRests.length, byAsset: {} };

  getRegularAccountsAssets(spotRests).forEach(asset => {
    addAssetCount(counts, asset.code);
  });

  return counts;
};

const checkLimits = (
  configurationCode: AccountConfigurationCodeDto,
  counts: OpenedCounts
): boolean => {
  const {
    allowedAccountsQuantityTotal,
    allowedAccountsQuantityByAsset,
    isMultiAsset,
    assets,
  } = configurationCode;

  if (
    allowedAccountsQuantityTotal !== null &&
    allowedAccountsQuantityTotal <= counts.total
  ) {
    return false;
  }

  if (
    allowedAccountsQuantityByAsset === null ||
    isMultiAsset ||
    !assets ||
    assets.length === 0
  ) {
    return true;
  }

  return assets.some(
    asset =>
      allowedAccountsQuantityByAsset >
      (counts.byAsset[asset.toLowerCase()] || 0)
  );
};

const useOpenAccountAvailability = (
  accountsRestsByType: AccountsRestsByType | null | undefined
) => {
  const {
    setCodesAvailability,
    resetAvailability,
    codesAvailability,
    openSpotIsAvailable,
    openInvestIsAvailable,
    openMarginIsAvailable,
    openEarnIsAvailable,
    spotCreateIsActive,
    investCreateIsActive,
    marginCreateIsActive,
    earnCreateIsActive,
  } = useOpenAccountAvailabilityStore();

  const { pendingAccounts } = usePendingAccountsStore();

  const {
    accountsConfigurations,
    isAccountsConfigurationsLoading,
    isAccountsConfigurationsError,
  } = useAccountsConfigurations();

  useEffect(() => {
    if (
      !accountsConfigurations ||
      isAccountsConfigurationsError ||
      !accountsRestsByType
    ) {
      resetAvailability();
      return;
    }

    const codes: Array<OpenAccountCodeAvailability> = [];

    accountsConfigurations.accountTypes.forEach(accountType => {
      const typeCode = accountType.accountTypeCode.toLowerCase();
      const pendingType = PENDING_TYPE_BY_CONFIG_TYPE[typeCode];

      accountType.accountCodes.forEach(configurationCode => {
        const counts =
          typeCode === 'spot'
            ? countSpotAccounts(accountsRestsByType.spot)
            : countMarginAccountsForCode(
                typeCode === 'invest' ||
                  typeCode === 'leverage' ||
                  typeCode === 'earn'
                  ? accountsRestsByType[typeCode]
                  : null,
                configurationCode.accountCode
              );

        countPendingForCode(
          counts,
          pendingAccounts,
          pendingType,
          configurationCode.accountCode
        );

        const canCreate =
          configurationCode.isCreateActive &&
          checkLimits(configurationCode, counts);

        codes.push({
          accountTypeCode: typeCode,
          accountCode: configurationCode.accountCode.toLowerCase(),
          isCreateActive: configurationCode.isCreateActive,
          canCreate,
        });
      });
    });

    setCodesAvailability(codes);
  }, [
    accountsConfigurations,
    isAccountsConfigurationsError,
    accountsRestsByType,
    pendingAccounts,
  ]);

  return {
    accountsConfigurations,
    isAccountsConfigurationsLoading,
    codesAvailability,
    openSpotIsAvailable,
    openInvestIsAvailable,
    openMarginIsAvailable,
    openEarnIsAvailable,
    spotCreateIsActive,
    investCreateIsActive,
    marginCreateIsActive,
    earnCreateIsActive,
    pendingAccounts,
  };
};

export { useOpenAccountAvailability };
